import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Award, CheckCircle2, ArrowRight, PhoneCall, FileText, Tag, MapPin, CalendarClock, Phone, Mail } from "lucide-react";
import Seo, { organizationSchema } from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { Button } from "@/components/ui/button";
import { easeOut } from "@/components/common/Reveal";
import { getFormation, getCategory, getFormationsByCategory, QUALIOPI_DEFAULTS, RNCP_ECSR } from "@/data/formations";
import { useLead } from "@/components/common/LeadModal";
import { SITE, CONTACTS } from "@/data/site";

function Block({ title, children, id }) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: easeOut }}
      className="scroll-mt-28 border-t border-slate-200 py-8 first:border-0 first:pt-0"
      data-testid={`block-${id}`}
    >
      <h2 className="font-display text-2xl font-bold text-brand-blue">{title}</h2>
      <div className="mt-4 leading-relaxed text-slate-600">{children}</div>
    </motion.section>
  );
}

export default function FormationDetail() {
  const { category, slug } = useParams();
  const { openLead } = useLead();
  const formation = getFormation(slug);
  const cat = getCategory(category);

  if (!formation || !cat || formation.category !== category) {
    return <Navigate to="/nos-formations" replace />;
  }

  const related = getFormationsByCategory(category).filter((f) => f.slug !== slug).slice(0, 3);

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: formation.title,
    description: formation.description,
    provider: { "@type": "Organization", name: SITE.name, sameAs: SITE.url },
  };

  const blocks = [
    { id: "objectifs", title: "Objectifs de la formation", render: () => (
      <>
        {formation.objectifsIntro && <p className="mb-4">{formation.objectifsIntro}</p>}
        {formation.objectifs && (
          <ul className="space-y-2.5">
            {formation.objectifs.map((o, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-red" />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        )}
      </>
    )},
    { id: "public", title: "Public concerné", value: formation.publicConcerne || QUALIOPI_DEFAULTS.publicConcerne },
    { id: "prerequis", title: "Prérequis", value: formation.prerequis },
    { id: "programme", title: "Programme & méthodes pédagogiques", value: formation.programme },
    { id: "modalites", title: "Modalités pédagogiques", value: formation.modalites || QUALIOPI_DEFAULTS.modalitesFormat },
    { id: "moyens", title: "Moyens techniques", value: formation.moyensTechniques || QUALIOPI_DEFAULTS.moyensTechniques },
    { id: "examen", title: "Modalités d'évaluation", value: formation.examen },
    { id: "certification", title: "Sanction de la formation", value: formation.certification },
    ...(formation.category === "formations-enseignants" ? [{
      id: "rncp",
      title: "Certification enregistrée au RNCP",
      render: () => (
        <div className="space-y-2 rounded-2xl border border-brand-blue/15 bg-brand-blue/5 p-5">
          <p><strong>Code :</strong> <a href={RNCP_ECSR.lien} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-red hover:underline">{RNCP_ECSR.code}</a></p>
          <p><strong>Intitulé :</strong> {RNCP_ECSR.intitule}</p>
          <p><strong>Niveau :</strong> {RNCP_ECSR.niveau}</p>
          <p><strong>Certificateur :</strong> {RNCP_ECSR.certificateur}</p>
          <p><strong>Date d'enregistrement :</strong> {RNCP_ECSR.enregistrement}</p>
          <p><strong>Date d'échéance :</strong> {RNCP_ECSR.echeance}</p>
          <p className="text-xs text-slate-500">Source : France Compétences.</p>
        </div>
      ),
    }] : []),
    { id: "debouches", title: "Débouchés", value: formation.debouches },
    { id: "inscription", title: "Modalités d'inscription", value: formation.modalitesInscription || QUALIOPI_DEFAULTS.modalitesInscription },
    { id: "delais", title: "Délais d'accès", value: formation.delaiAcces || QUALIOPI_DEFAULTS.delaiAcces },
    { id: "lieu", title: "Lieu de formation", value: formation.lieu || QUALIOPI_DEFAULTS.lieu },
    { id: "accessibilite", title: "Accessibilité handicap", value: formation.accessibilite || QUALIOPI_DEFAULTS.accessibilite },
    { id: "financement", title: "Modalités de financement", value: formation.financement },
    { id: "reglementaire", title: "Informations réglementaires", value: formation.reglementaire },
    { id: "contact", title: "Contact", render: () => (
      <div className="space-y-2">
        <p>Une question sur cette formation ? Notre équipe vous répond :</p>
        <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-red" /> <a className="hover:text-brand-blue" href={`tel:${CONTACTS.enseignants.telephone.replace(/\s/g, "")}`}>{CONTACTS.enseignants.telephone}</a> (enseignants) · <a className="hover:text-brand-blue" href={`tel:${CONTACTS.groupeLourd.telephone.replace(/\s/g, "")}`}>{CONTACTS.groupeLourd.telephone}</a> (groupe lourd)</p>
        <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand-red" /> <a className="hover:text-brand-blue" href={`mailto:${CONTACTS.enseignants.email}`}>{CONTACTS.enseignants.email}</a></p>
      </div>
    )},
  ];

  return (
    <>
      <Seo
        title={formation.title}
        description={formation.description}
        path={`/nos-formations/${category}/${slug}`}
        image={formation.image}
        schema={courseSchema}
      />
      <PageHero
        breadcrumb={[
          { label: "Accueil", to: "/" },
          { label: "Nos formations", to: "/nos-formations" },
          { label: cat.title, to: `/nos-formations/${cat.slug}` },
          { label: formation.title },
        ]}
        eyebrow={cat.title}
        title={formation.title}
        intro={formation.description}
      />

      <div className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 lg:grid-cols-12 lg:px-8">
          {/* Content */}
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-3xl">
              <img src={formation.image} alt={formation.title} className="h-72 w-full object-cover sm:h-96" />
            </div>
            <div className="mt-10">
              {blocks.map((b) =>
                b.render ? (
                  <Block key={b.id} id={b.id} title={b.title}>{b.render()}</Block>
                ) : b.value ? (
                  <Block key={b.id} id={b.id} title={b.title}>
                    <p>{b.value}</p>
                  </Block>
                ) : null
              )}
            </div>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_16px_48px_rgba(0,35,102,0.08)]">
                <h3 className="font-display text-lg font-bold text-brand-blue">L'essentiel</h3>
                <ul className="mt-5 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/5 text-brand-blue">
                      <Clock className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-slate-400">Durée</p>
                      <p className="font-medium text-brand-blue">{formation.duree}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/5 text-brand-blue">
                      <Award className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-slate-400">Certification</p>
                      <p className="font-medium text-brand-blue">{formation.certificationShort}</p>
                    </div>
                  </li>
                  {formation.tarif && (
                    <li className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-red/10 text-brand-red">
                        <Tag className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-slate-400">Tarif</p>
                        <p className="font-medium text-brand-blue">{formation.tarif}</p>
                      </div>
                    </li>
                  )}
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/5 text-brand-blue">
                      <CalendarClock className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-slate-400">Délais d'accès</p>
                      <p className="font-medium text-brand-blue">Session sous 15 jours après validation du dossier</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/5 text-brand-blue">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-slate-400">Lieu</p>
                      <p className="font-medium text-brand-blue">Centres d'Île-de-France</p>
                    </div>
                  </li>
                </ul>

                <div className="mt-6 space-y-3">
                  <Button
                    onClick={() => openLead("inscription", { formation: formation.title })}
                    className="group w-full rounded-full bg-brand-red py-6 text-base font-semibold hover:bg-brand-red-light"
                    data-testid="detail-cta-inscription"
                  >
                    S'inscrire
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button
                    onClick={() => openLead("devis", { formation: formation.title })}
                    variant="outline"
                    className="w-full rounded-full border-2 border-brand-blue py-6 text-base font-semibold text-brand-blue hover:bg-brand-blue hover:text-white"
                    data-testid="detail-cta-devis"
                  >
                    Demander un devis
                  </Button>
                  <button
                    onClick={() => openLead("rappel", { formation: formation.title })}
                    className="flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-medium text-slate-500 transition-colors hover:text-brand-red"
                    data-testid="detail-cta-rappel"
                  >
                    <PhoneCall className="h-4 w-4" /> Être rappelé
                  </button>
                </div>
              </div>

              {related.length > 0 && (
                <div className="rounded-3xl border border-slate-200 bg-slate-50/60 p-7">
                  <h3 className="flex items-center gap-2 font-display text-base font-bold text-brand-blue">
                    <FileText className="h-4 w-4 text-brand-red" /> Formations liées
                  </h3>
                  <ul className="mt-4 space-y-2">
                    {related.map((r) => (
                      <li key={r.slug}>
                        <Link
                          to={`/nos-formations/${category}/${r.slug}`}
                          className="block rounded-lg px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-white hover:text-brand-blue"
                        >
                          {r.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
